/**
 * Quests: what there is, and how one is written.
 *
 * The same two screens as items and characters. A quest is a name, a summary and
 * a list of steps, and a step is a line for the journal, what finishes it, and
 * where it points whoever is following it.
 *
 * Nothing is sent while somebody types. Every keystroke used to go to the server
 * and come back as a fresh set of definitions, which redrew the box under the
 * cursor and put the cursor at the end of it: writing a sentence was not
 * possible. What is typed is held here until it is kept.
 */

import type { ItemDefinitionWire, NpcDefinitionWire, QuestWire } from '@inverse/protocol';
import { DEEDS } from './deeds';
import { ItemPicker } from './ItemPicker';
import { useEffect, useRef, useState } from 'react';
import type { AdminCommand } from '../Admin';
import { Explain } from '../Explain';
import { QuestCanvas, type CanvasView, type Chosen } from './QuestCanvas';
import { useSplitter } from './splitter';
import { Conditions, Effects, type Effect } from './Dialogue';
import {
  blankQuest,
  blankStep,
  draftFrom,
  problemWith,
  questFrom,
  SKILLS,
  markCleared,
  markKindOf,
  triggerLine,
  TRIGGER_ABOUT,
  TRIGGER_LABELS,
  WORN_LABELS,
  type NeedDraft,
  type QuestDraft,
  type SavedQuest,
  type StepDraft,
  type TriggerKind,
} from './quests';

export function EditorQuests({
  quests,
  progress,
  items,
  characters,
  objects,
  named,
  detail,
  saves,
  openQuest,
  onEditTalk,
  onCommand,
}: {
  quests: readonly QuestWire[];
  /** How far along the person editing is, so a step can be tried from a point. */
  progress: readonly { id: string; step: number }[];
  items: readonly ItemDefinitionWire[];
  characters: readonly NpcDefinitionWire[];
  /** For naming the scenery a deed goal is about. */
  objects: readonly { id: number; name: string }[];
  /** The ground somebody has named, for a step that points at a place. */
  named: readonly { id: number; name: string }[];
  /** The quest the server last sent in full, which is what editing works from. */
  detail: SavedQuest | null;
  saves: number;
  /** A quest to open, asked for from the conversation editor. */
  openQuest: string | null;
  /** Opens the conversation of a character, for a step that waits on one. */
  onEditTalk: (npcId: number) => void;
  onCommand: AdminCommand;
}): JSX.Element {
  const [chosen, setChosen] = useState<string | null>(null);
  const [draft, setDraft] = useState<QuestDraft | null>(null);
  const [naming, setNaming] = useState('');
  const [saying, setSaying] = useState('');
  const [seenSaves, setSeenSaves] = useState(saves);

  // Somebody elsewhere asked for a particular quest. Taken once, so choosing
  // another here afterwards is not undone by the request that opened this.
  const [taken, setTaken] = useState<string | null>(null);
  if (openQuest !== null && openQuest !== taken) {
    setTaken(openQuest);
    setChosen(openQuest);
    setDraft(null);
    onCommand('questDetails', {}, { quest: openQuest });
  }

  if (seenSaves !== saves) {
    setSeenSaves(saves);
    setSaying('');
    if (detail) setChosen(detail.id);
  }

  /**
   * A quest writes itself down, the way a conversation does.
   *
   * Nobody should have to remember to press Save, and a draft that only exists
   * in a panel is a draft that something else can quietly overwrite. Sent a
   * moment after the typing stops rather than on every keystroke.
   */
  const pending = useRef<number | null>(null);

  /**
   * Whether something typed here is still on its way to the world.
   *
   * Held as state rather than only on the timer, because the answer is needed
   * while deciding what to draw: a copy arriving from the world must not undo
   * something typed a moment ago that has not been sent yet.
   */
  const [sending, setSending] = useState(false);

  /**
   * The quest as the world last sent it, for whichever one is being written.
   *
   * A quest changes from two places: here, and the conversation editor filling
   * in the step a line finishes. Whichever one moved last, the other has to
   * hear about it, or the next save from the stale one writes the change away.
   */
  const [seenDetail, setSeenDetail] = useState<SavedQuest | null>(null);
  if (detail !== seenDetail) {
    setSeenDetail(detail);

    // Taken only when nothing is waiting to go out. Anything typed in the last
    // moment is still on its way to the world, and would be undone by the copy
    // that was sent before it.
    if (detail && draft && draft.id === detail.id && !sending) {
      setDraft(draftFrom(detail));
    }
  }

  const save = (held: QuestDraft): void => {
    const problem = problemWith(held);
    if (problem) {
      setSaying(problem);
      return;
    }

    setSaying('');
    onCommand('saveQuest', {}, { json: JSON.stringify(questFrom(held)) });
  };

  const written = (next: QuestDraft): void => {
    setDraft(next);
    setSending(true);

    if (pending.current !== null) window.clearTimeout(pending.current);
    pending.current = window.setTimeout(() => {
      pending.current = null;
      setSending(false);
      save(next);
    }, 700);
  };

  useEffect(
    () => () => {
      if (pending.current !== null) window.clearTimeout(pending.current);
    },
    [],
  );

  if (draft) {
    return (
      <QuestForm
        draft={draft}
        items={items}
        characters={characters}
        objects={objects}
        quests={quests}
        named={named}
        onEditTalk={onEditTalk}
        saying={saying}
        at={progress.find((entry) => entry.id === draft.id)?.step ?? 0}
        onJump={(step) => onCommand('setProgress', { step }, { quest: draft.id })}
        onChange={written}
        onSave={() => {
          if (pending.current !== null) window.clearTimeout(pending.current);
          pending.current = null;
          setSending(false);
          save(draft);
        }}
        onCancel={() => {
          setDraft(null);
          setSaying('');
        }}
      />
    );
  }

  const open = detail && detail.id === chosen ? detail : null;
  const at = progress.find((entry) => entry.id === chosen)?.step ?? 0;

  return (
    <div className="quest-browse">
      <div className="editor-drop-add">
        <input
          className="editor-search"
          type="text"
          value={naming}
          placeholder="A new quest"
          onChange={(event) => setNaming(event.target.value)}
        />
        <button
          type="button"
          className="wide primary"
          disabled={naming.trim().length === 0}
          onClick={() => {
            setDraft(blankQuest(naming));
            setNaming('');
            setChosen(null);
          }}
        >
          Write it
        </button>
      </div>

      {/* The list keeps a size of its own. Sharing the panel's height with the
          quest below it meant that opening one with twenty eight steps squeezed
          the list to nothing, and the only way back to another quest was to
          leave the tab and come back. */}
      <div className="editor-list quest-list">
        {quests.map((quest) => (
          <button
            key={quest.id}
            type="button"
            className={quest.id === chosen ? 'editor-row on' : 'editor-row'}
            onClick={() => {
              setChosen(quest.id);
              setDraft(null);
              onCommand('questDetails', {}, { quest: quest.id });
            }}
          >
            <span>{quest.name}</span>
            <span className="editor-row-id">
              {quest.steps.length === 1 ? '1 step' : `${quest.steps.length} steps`}
            </span>
          </button>
        ))}
      </div>

      {quests.length === 0 ? <p className="hint">Nothing written yet.</p> : null}

      {open ? (
        <div className="editor-facts">
          <h3>{open.name}</h3>
          <p className="hint">{open.summary || 'No summary.'}</p>
          {open.hint ? <p className="hint">Starts: {open.hint}</p> : null}
          {open.requirements && open.requirements.length > 0 ? (
            <p className="hint">Asks for: {open.requirements.join(', ')}</p>
          ) : null}

          {/* What it is, said in a line. The shape of it is on the canvas, and
              the canvas is where it is written, so the list is a list. */}
          <p className="hint">
            {open.steps.length === 1 ? '1 step' : `${open.steps.length} steps`}
            {at === 0
              ? ', not started'
              : at > open.steps.length
                ? ', finished'
                : `, you are on step ${at}`}
          </p>

          <button type="button" className="wide primary" onClick={() => setDraft(draftFrom(open))}>
            Open It
          </button>

          <button
            type="button"
            className="wide danger"
            onClick={() => onCommand('removeQuest', {}, { quest: open.id })}
          >
            Remove this quest
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** The form: a name, a summary, and the steps. */
function QuestForm({
  draft,
  items,
  characters,
  objects,
  quests,
  named,
  onEditTalk,
  saying,
  at,
  onJump,
  onChange,
  onSave,
  onCancel,
}: {
  draft: QuestDraft;
  items: readonly ItemDefinitionWire[];
  characters: readonly NpcDefinitionWire[];
  /** For naming the scenery a deed goal is about. */
  objects: readonly { id: number; name: string }[];
  /** Every quest there is, for one that will not begin without another. */
  quests: readonly QuestWire[];
  /** The ground somebody has named, for a step that points at a place. */
  named: readonly { id: number; name: string }[];
  onEditTalk: (npcId: number) => void;
  saying: string;
  /** Which step the character is on, so the toolbar can say where they are. */
  at: number;
  onJump: (step: number) => void;
  onChange: (draft: QuestDraft) => void;
  onSave: () => void;
  onCancel: () => void;
}): JSX.Element {
  const set = (changed: Partial<QuestDraft>): void => onChange({ ...draft, ...changed });

  /**
   * Which node the panel beside the canvas is writing.
   *
   * The quest itself is a node too, which is what the panel shows when no step
   * is picked: a quest's name and summary are as much a part of it as its
   * steps, and putting them on a node keeps the whole thing one picture rather
   * than a form with a diagram in the middle of it.
   */
  const [chosen, setChosen] = useState<Chosen>(
    draft.steps.length > 0 ? { kind: 'step', step: 1 } : { kind: 'quest' },
  );

  /** The same answer counted from nought, which is what the step form works in. */
  const openStep = chosen.kind === 'step' ? chosen.step - 1 : null;

  /** Where the canvas is looking, and how much room the panel beside it has. */
  const [view, setView] = useState<CanvasView>({ x: 20, y: 24, zoom: 1 });
  const split = useSplitter('quest', { initial: 300, min: 220, max: 760 });

  // For the one line a folded step shows about itself.
  const nameOfItem = (id: number): string =>
    items.find((entry) => entry.id === id)?.name ?? (id > 0 ? `#${id}` : 'nothing');
  const nameOfNpc = (id: number): string =>
    characters.find((entry) => entry.id === id)?.name ?? (id > 0 ? `#${id}` : 'nothing');

  const setStep = (at: number, changed: Partial<StepDraft>): void => {
    set({
      steps: draft.steps.map((step, index) => (index === at ? { ...step, ...changed } : step)),
    });
  };

  /** Where a step goes, for drawing the canvas. */
  const canvasSteps = draft.steps.map((step) => ({
    text: step.text,
    waits: triggerLine(step, nameOfItem, nameOfNpc),
    branches: step.branches.map((branch) => ({
      step: Number(branch.step) || 0,
      conditional: branch.flag.trim().length > 0,
    })),
  }));

  /**
   * What the end of the quest amounts to, in one line for the node.
   *
   * So the Finish node says whether anything has been written on it rather than
   * looking the same whether the quest hands over a sword or nothing at all.
   */
  const rewardLines = draft.rewards.split('\n').filter((line) => line.trim().length > 0).length;
  const given: string[] = [];
  if (rewardLines > 0) given.push(rewardLines === 1 ? '1 reward' : `${rewardLines} rewards`);
  if (draft.does.length > 0) {
    given.push(draft.does.length === 1 ? '1 thing done' : `${draft.does.length} things done`);
  }

  const ending = {
    text: draft.finishedText,
    waits: given.length > 0 ? given.join(', ') : 'Nothing given yet',
  };

  const addAfter = (step: number): void => {
    const made = [...draft.steps];
    made.splice(step, 0, blankStep());
    setChosen({ kind: 'step', step: step + 1 });
    set({ steps: made });
  };

  return (
    <div className="conversation">
      {/* Everything that acts on the quest as a whole, on one row above the
          canvas: what it is called, where the character is in it, and the two
          ways out. */}
      <div className="conversation-bar">
        <span className="conversation-who">{draft.name || 'A new quest'}</span>
        <span className="editor-row-id">
          {draft.steps.length === 1 ? '1 step' : `${draft.steps.length} steps`}
        </span>

        <button
          type="button"
          className="editor-tab"
          title="Put a step at the end"
          onClick={() => addAfter(draft.steps.length)}
        >
          Add Step
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Put the canvas back over the beginning of the quest"
          onClick={() => setView({ x: 20, y: 24, zoom: 1 })}
        >
          Recentre
        </button>

        {/* Trying it from part way along, rather than playing back up to it
            every time a line is changed. One box that says where the character
            actually is, rather than a row of buttons that did not. */}
        <label className="conversation-jump">
          <span>Jump to Step</span>
          <select
            value={String(Math.min(at, draft.steps.length + 1))}
            title="Put your character at this point in the quest"
            onChange={(event) => onJump(Number(event.target.value))}
          >
            <option value="0">Unstarted</option>
            {draft.steps.map((step, index) => (
              <option key={index} value={index + 1}>
                {index + 1}. {step.text.slice(0, 40) || 'Nothing written yet'}
              </option>
            ))}
            <option value={draft.steps.length + 1}>Finished</option>
          </select>
        </label>

        <button
          type="button"
          className="editor-tab primary"
          title="Save this quest"
          onClick={onSave}
        >
          Save
        </button>
        <button
          type="button"
          className="editor-tab"
          title="Back to the quest list"
          onClick={onCancel}
        >
          Back
        </button>
      </div>

      <div className="conversation-body">
        <QuestCanvas
          name={draft.name}
          steps={canvasSteps}
          ending={ending}
          chosen={chosen}
          at={at}
          view={view}
          onView={setView}
          onChoose={setChosen}
          onAddAfter={addAfter}
          onBranch={(step) => {
            const held = draft.steps[step - 1];
            if (!held) return;
            setChosen({ kind: 'step', step });
            setStep(step - 1, {
              branches: [
                ...held.branches,
                { flag: '', is: 'yes', step: String(Math.min(step + 1, draft.steps.length)) },
              ],
            });
          }}
          onMove={(step, by) => {
            const to = step - 1 + by;
            if (to < 0 || to >= draft.steps.length) return;
            setChosen({ kind: 'step', step: to + 1 });
            set({ steps: swapped(draft.steps, step - 1, to) });
          }}
          onRemove={(step) => {
            setChosen({ kind: 'quest' });
            set({ steps: draft.steps.filter((_, index) => index !== step - 1) });
          }}
        />

        {/* Dragged to give the details more room, or to give it back to the
            canvas. Double click puts it where it started. */}
        <div
          className={split.dragging ? 'conversation-grip on' : 'conversation-grip'}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the details"
          title="Drag to resize, double click to reset"
          onPointerDown={split.onPointerDown}
          onDoubleClick={split.reset}
        />

        <div className="conversation-side" style={{ flexBasis: `${split.width}px` }}>
          {/* The end of the quest, written on the node that is the end of it.
              What the notice says, what it lists as given, and what the world
              actually does about it: three parts of one moment, which is why
              they sit together rather than on the form for the quest's name. */}
          {chosen.kind === 'end' ? (
            <>
              <p className="hint">Anything reaching here has finished the quest.</p>

              <label className="editor-field">
                <span>
                  What the notice says
                  <Explain>
                    Read out on the completion notice, in the quest's own voice. Left empty it
                    simply says the quest is complete, which is true and says nothing.
                  </Explain>
                </span>
                <textarea
                  className="editor-text"
                  rows={3}
                  value={draft.finishedText}
                  placeholder="You have learned everything this island can teach you."
                  onChange={(event) => set({ finishedText: event.target.value.slice(0, 300) })}
                />
              </label>

              <label className="editor-field">
                <span>
                  What it gave
                  <Explain>
                    One a line, listed on the notice. A number in front says how many: "4 x Copper
                    bar". These are the words on the notice; handing the things over is done below.
                  </Explain>
                </span>
                <textarea
                  className="editor-text"
                  rows={3}
                  value={draft.rewards}
                  placeholder={'900 Woodcutting experience\n1 x Copper shortblade'}
                  onChange={(event) => set({ rewards: event.target.value.slice(0, 800) })}
                />
              </label>

              {/* And what the world does about it. The same effects a line of
                  conversation carries: this is where the reward is actually
                  handed over, rather than on whichever step happens to be last. */}
              <h4>
                What finishing it does
                <Explain>
                  Run the moment the quest is finished, whichever step led here. Handing the reward
                  over, granting experience, setting a flag: the same list a line of conversation
                  carries.
                </Explain>
              </h4>

              <Effects
                held={draft.does}
                items={items}
                quests={quests}
                shops={[]}
                characters={characters}
                objects={objects}
                onChange={(does: Effect[]) => set({ does })}
              />

              <button
                type="button"
                className="wide"
                onClick={() => set({ does: [...draft.does, { setFlag: '', value: 'yes' }] })}
              >
                Do something when it is finished
              </button>
            </>
          ) : null}

          {chosen.kind === 'quest' ? (
            <>
              <label className="editor-field">
                <span>Name</span>
                <input
                  type="text"
                  value={draft.name}
                  onChange={(event) => set({ name: event.target.value.slice(0, 64) })}
                />
              </label>

              <label className="editor-field">
                <span>Summary</span>
                <textarea
                  className="editor-text"
                  rows={3}
                  value={draft.summary}
                  onChange={(event) => set({ summary: event.target.value.slice(0, 300) })}
                />
              </label>

              <label className="editor-field">
                <span>
                  How it starts
                  <Explain>
                    What the journal tells somebody who has not started it yet. Left empty, the
                    journal says nothing about where to go.
                  </Explain>
                </span>
                <input
                  type="text"
                  value={draft.hint}
                  placeholder="Speak to the fisherman on the dock"
                  onChange={(event) => set({ hint: event.target.value.slice(0, 200) })}
                />
              </label>
              <label className="editor-field">
                <span>
                  Asks for
                  <Explain>
                    Only what the steps do not already say. Levels, items and fights are worked out
                    from the steps and added to these on their own.
                  </Explain>
                </span>
                <textarea
                  className="editor-text"
                  rows={3}
                  value={draft.requirements}
                  placeholder="One a line"
                  onChange={(event) => set({ requirements: event.target.value.slice(0, 800) })}
                />
              </label>

              <h4>
                Kept out of the journal until
                <Explain>
                  A quest nobody could stumble on yet stays unlisted until all of these are true, so
                  the journal is not forty names, most of them out of reach. Nothing here means it
                  is always listed.
                </Explain>
              </h4>
              <Conditions
                held={draft.hiddenUntil}
                items={items}
                quests={quests}
                characters={characters}
                objects={objects}
                onChange={(hiddenUntil) => set({ hiddenUntil })}
              />
              {draft.hiddenUntil.length < 8 ? (
                <button
                  type="button"
                  className="editor-tab"
                  onClick={() => set({ hiddenUntil: [...draft.hiddenUntil, {}] })}
                >
                  Add A Condition
                </button>
              ) : null}

              <label className="editor-field">
                <span>
                  Follow it as soon as it begins
                  <Explain>
                    The marks go up without anybody pressing anything, for a quest meant to lead
                    somebody about. Somebody who wants that for every quest can say so in their own
                    settings instead.
                  </Explain>
                </span>
                <input
                  type="checkbox"
                  checked={draft.followed}
                  onChange={(event) => set({ followed: event.target.checked })}
                />
              </label>

              {/* The same thing again, in the form the world can check. Apart from the
          box above on purpose: what a player reads and what the world enforces
          are different jobs, and one of them is prose. */}
              <h4>
                Will not begin without
                <Explain>
                  Checked when somebody starts it, and never after: a requirement that could stop a
                  quest halfway is a quest somebody can be stranded in.
                </Explain>
              </h4>

              {draft.needs.map((need, which) => (
                <div className="editor-brushes" key={which}>
                  <select
                    value={need.kind}
                    onChange={(event) =>
                      set({
                        needs: draft.needs.map((one, index) =>
                          index === which
                            ? { ...one, kind: event.target.value as NeedDraft['kind'] }
                            : one,
                        ),
                      })
                    }
                  >
                    <option value="quest">Another quest finished</option>
                    <option value="skill">A level</option>
                  </select>

                  {need.kind === 'quest' ? (
                    <select
                      value={need.quest}
                      onChange={(event) =>
                        set({
                          needs: draft.needs.map((one, index) =>
                            index === which ? { ...one, quest: event.target.value } : one,
                          ),
                        })
                      }
                    >
                      <option value="">Choose a quest</option>
                      {quests
                        .filter((one) => one.id !== draft.id)
                        .map((one) => (
                          <option key={one.id} value={one.id}>
                            {one.name}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <>
                      <select
                        value={need.skill}
                        onChange={(event) =>
                          set({
                            needs: draft.needs.map((one, index) =>
                              index === which
                                ? { ...one, skill: event.target.value as NeedDraft['skill'] }
                                : one,
                            ),
                          })
                        }
                      >
                        {SKILLS.map((skill) => (
                          <option key={skill} value={skill}>
                            {skill}
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={need.level}
                        onChange={(event) =>
                          set({
                            needs: draft.needs.map((one, index) =>
                              index === which ? { ...one, level: event.target.value } : one,
                            ),
                          })
                        }
                      />
                    </>
                  )}

                  <button
                    type="button"
                    className="editor-drop-remove"
                    title="Delete this one"
                    onClick={() =>
                      set({ needs: draft.needs.filter((_, index) => index !== which) })
                    }
                  >
                    ✕
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="wide"
                onClick={() =>
                  set({
                    needs: [
                      ...draft.needs,
                      { kind: 'quest', quest: '', skill: 'woodcutting', level: '1' },
                    ],
                  })
                }
              >
                Ask for something first
              </button>
            </>
          ) : null}

          {/* And the settings for whichever step is picked, in the same panel. */}
          {((): JSX.Element | null => {
            const atStep = openStep;
            const step = atStep === null ? undefined : draft.steps[atStep];

            if (atStep === null || !step) return null;

            const at = atStep;

            return (
              <div className="editor-step open">
                <label className="editor-field">
                  <span>What to do now</span>
                  <input
                    type="text"
                    value={step.text}
                    placeholder="What to do now"
                    onChange={(event) => setStep(at, { text: event.target.value.slice(0, 200) })}
                  />
                </label>
                <>
                  <label className="editor-field">
                    <span>Goal</span>
                    <select
                      value={step.trigger}
                      onChange={(event) =>
                        setStep(at, { trigger: event.target.value as TriggerKind })
                      }
                    >
                      {(Object.keys(TRIGGER_LABELS) as TriggerKind[]).map((kind) => (
                        <option key={kind} value={kind}>
                          {TRIGGER_LABELS[kind]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <p className="hint">{TRIGGER_ABOUT[step.trigger]}</p>

                  {step.trigger === 'none' ? (
                    <>
                      <label className="editor-field">
                        <span>Speaker</span>
                        <select
                          value={step.markNpcId}
                          onChange={(event) => setStep(at, { markNpcId: event.target.value })}
                        >
                          <option value="0">Anybody</option>
                          {[...characters]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((entry) => (
                              <option key={entry.id} value={entry.id}>
                                {entry.name}
                              </option>
                            ))}
                        </select>
                      </label>

                      {/* The conversation is the other half of a dialogue goal, so it is
                  one press away rather than somewhere else entirely. A line
                  dragged out of the tree lands on the mark below. */}
                      <button
                        type="button"
                        className="wide"
                        disabled={Number(step.markNpcId) <= 0}
                        onClick={() => onEditTalk(Number(step.markNpcId))}
                      >
                        Open their conversation
                      </button>

                      <label
                        className="editor-field"
                        onDragOver={(event) => {
                          if (event.dataTransfer.types.includes('application/inverse-line')) {
                            event.preventDefault();
                          }
                        }}
                        onDrop={(event) => {
                          const line = event.dataTransfer.getData('application/inverse-line');
                          if (line.length === 0) return;

                          event.preventDefault();

                          // The node is carried with the character it belongs to, so a
                          // step written this way names the right speaker even when the
                          // dropdown above still says Anybody.
                          const carried = event.dataTransfer.getData('application/inverse-npc');
                          const npcId = Number(carried) > 0 ? carried : step.markNpcId;

                          setStep(at, {
                            markNpcId: npcId,
                            flag: `said.${npcId}.${line}`,
                            flagIs: 'yes',
                          });
                        }}
                      >
                        <span>
                          At the line
                          <Explain>
                            Drag a node out of the conversation window by its handle and drop it
                            here. Reaching that node writes a mark down, and the step waits for that
                            mark.
                          </Explain>
                        </span>
                        <input
                          type="text"
                          value={
                            step.flag.startsWith('said.')
                              ? step.flag.split('.').slice(2).join('.')
                              : ''
                          }
                          placeholder="any line"
                          readOnly
                        />
                      </label>

                      <label className="editor-field">
                        <span>Also Carrying</span>
                        <ItemPicker
                          items={items}
                          value={Number(step.alsoItem)}
                          allowNothing
                          nothingLabel="anything"
                          onPick={(itemId) => setStep(at, { alsoItem: String(itemId) })}
                        />
                      </label>
                      <label className="editor-field">
                        <span>Also Wearing</span>
                        <ItemPicker
                          items={items}
                          value={Number(step.alsoWorn)}
                          allowNothing
                          nothingLabel="anything"
                          onPick={(itemId) => setStep(at, { alsoWorn: String(itemId) })}
                        />
                      </label>
                    </>
                  ) : null}

                  {step.trigger === 'wearing' ? (
                    <>
                      <label className="editor-field">
                        <span>Wearing</span>
                        <ItemPicker
                          items={items}
                          value={Number(step.itemId)}
                          onPick={(itemId) => setStep(at, { itemId: String(itemId) })}
                        />
                      </label>

                      {/* Gear or belt. The same pickaxe in two different places is two
                  different things to have done, and a quest that says to put
                  your tools on your belt has to be able to say which it means. */}
                      <div className="editor-choice" role="group" aria-label="Equip Location">
                        {(['gear', 'belt'] as const).map((place) => (
                          <button
                            type="button"
                            key={place}
                            className={step.wornOn === place ? 'editor-tab on' : 'editor-tab'}
                            aria-pressed={step.wornOn === place}
                            onClick={() => setStep(at, { wornOn: place })}
                          >
                            {WORN_LABELS[place]}
                          </button>
                        ))}
                      </div>

                      {/* And anything else it asks them to have on. */}
                      {step.alsoWear.map((also, which) => (
                        <div className="editor-bracket-row" key={which}>
                          <button
                            type="button"
                            className={also.or ? 'editor-join or' : 'editor-join'}
                            title="And / Or against the line above"
                            onClick={() =>
                              setStep(at, {
                                alsoWear: step.alsoWear.map((one, index) =>
                                  index === which ? { ...one, or: !one.or } : one,
                                ),
                              })
                            }
                          >
                            {also.or ? 'or' : 'and'}
                          </button>

                          <div className="editor-condition">
                            <ItemPicker
                              items={items}
                              value={Number(also.itemId)}
                              onPick={(itemId) =>
                                setStep(at, {
                                  alsoWear: step.alsoWear.map((one, index) =>
                                    index === which ? { ...one, itemId: String(itemId) } : one,
                                  ),
                                })
                              }
                            />

                            {(['gear', 'belt'] as const).map((place) => (
                              <button
                                type="button"
                                key={place}
                                className={also.on === place ? 'editor-tab on' : 'editor-tab'}
                                aria-pressed={also.on === place}
                                onClick={() =>
                                  setStep(at, {
                                    alsoWear: step.alsoWear.map((one, index) =>
                                      index === which ? { ...one, on: place } : one,
                                    ),
                                  })
                                }
                              >
                                {WORN_LABELS[place]}
                              </button>
                            ))}

                            <button
                              type="button"
                              className="editor-tab"
                              aria-label="Remove"
                              onClick={() =>
                                setStep(at, {
                                  alsoWear: step.alsoWear.filter((_, index) => index !== which),
                                })
                              }
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="editor-tab quiet"
                        onClick={() =>
                          setStep(at, {
                            alsoWear: [...step.alsoWear, { itemId: '0', on: 'gear', or: false }],
                          })
                        }
                      >
                        And something else
                      </button>
                    </>
                  ) : null}

                  {step.trigger === 'place' ? (
                    <div className="tool-row">
                      {(
                        [
                          ['areaTx', 'Left'],
                          ['areaTz', 'Top'],
                          ['areaWidth', 'Across'],
                          ['areaHeight', 'Down'],
                        ] as const
                      ).map(([field, label]) => (
                        <label className="editor-number" key={field}>
                          <span>{label}</span>
                          <input
                            type="number"
                            value={step[field]}
                            onChange={(event) => setStep(at, { [field]: event.target.value })}
                          />
                        </label>
                      ))}
                    </div>
                  ) : null}

                  {step.trigger === 'carry' ? (
                    <>
                      <label className="editor-field">
                        <span>Item</span>
                        <select
                          value={step.itemId}
                          onChange={(event) => setStep(at, { itemId: event.target.value })}
                        >
                          <option value="0">Choose an item</option>
                          {[...items]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((entry) => (
                              <option key={entry.id} value={entry.id}>
                                {entry.name}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label className="editor-field">
                        <span>Quantity</span>
                        <input
                          type="text"
                          value={step.count}
                          onChange={(event) =>
                            setStep(at, { count: event.target.value.replace(/[^0-9]/g, '') })
                          }
                        />
                      </label>

                      {/* More things it asks for. "Two logs and a rope" and "either a
                  hatchet or an axe" are both ordinary asks, and one item with
                  one count could say neither of them. */}
                      {step.alsoCarry.map((also, which) => (
                        <div className="editor-bracket-row" key={which}>
                          <button
                            type="button"
                            className={also.or ? 'editor-join or' : 'editor-join'}
                            title="And / Or against the line above"
                            onClick={() =>
                              setStep(at, {
                                alsoCarry: step.alsoCarry.map((one, index) =>
                                  index === which ? { ...one, or: !one.or } : one,
                                ),
                              })
                            }
                          >
                            {also.or ? 'or' : 'and'}
                          </button>

                          <div className="editor-condition">
                            <select
                              value={also.itemId}
                              onChange={(event) =>
                                setStep(at, {
                                  alsoCarry: step.alsoCarry.map((one, index) =>
                                    index === which ? { ...one, itemId: event.target.value } : one,
                                  ),
                                })
                              }
                            >
                              <option value="0">Choose an item</option>
                              {[...items]
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((entry) => (
                                  <option key={entry.id} value={entry.id}>
                                    {entry.name}
                                  </option>
                                ))}
                            </select>

                            <label className="editor-number">
                              <span>qty</span>
                              <input
                                type="number"
                                min={1}
                                value={also.count}
                                onChange={(event) =>
                                  setStep(at, {
                                    alsoCarry: step.alsoCarry.map((one, index) =>
                                      index === which ? { ...one, count: event.target.value } : one,
                                    ),
                                  })
                                }
                              />
                            </label>

                            <button
                              type="button"
                              className="editor-tab"
                              onClick={() =>
                                setStep(at, {
                                  alsoCarry: step.alsoCarry.filter((_, index) => index !== which),
                                })
                              }
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="editor-tab quiet"
                        onClick={() =>
                          setStep(at, {
                            alsoCarry: [...step.alsoCarry, { itemId: '0', count: '1', or: false }],
                          })
                        }
                      >
                        And something else
                      </button>
                    </>
                  ) : null}

                  {step.trigger === 'kill' ? (
                    <>
                      <label className="editor-field">
                        <span>Item</span>
                        <select
                          value={step.npcId}
                          onChange={(event) => setStep(at, { npcId: event.target.value })}
                        >
                          <option value="0">Choose a character</option>
                          {[...characters]
                            .filter((entry) => entry.combatLevel > 0)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((entry) => (
                              <option key={entry.id} value={entry.id}>
                                {entry.name}
                              </option>
                            ))}
                        </select>
                      </label>
                      <label className="editor-field">
                        <span>Quantity</span>
                        <input
                          type="text"
                          value={step.count}
                          onChange={(event) =>
                            setStep(at, { count: event.target.value.replace(/[^0-9]/g, '') })
                          }
                        />
                      </label>
                    </>
                  ) : null}

                  {step.trigger === 'level' ? (
                    <>
                      <label className="editor-field">
                        <span>Skill</span>
                        <select
                          value={step.skill}
                          onChange={(event) =>
                            setStep(at, { skill: event.target.value as StepDraft['skill'] })
                          }
                        >
                          {SKILLS.map((skill) => (
                            <option key={skill} value={skill}>
                              {skill}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="editor-field">
                        <span>Level</span>
                        <input
                          type="text"
                          value={step.level}
                          onChange={(event) =>
                            setStep(at, { level: event.target.value.replace(/[^0-9]/g, '') })
                          }
                        />
                      </label>
                    </>
                  ) : null}

                  {step.trigger === 'flag' ? (
                    <>
                      <label className="editor-field">
                        <span>Flag</span>
                        <input
                          type="text"
                          value={step.flag}
                          placeholder="told.ardor"
                          onChange={(event) =>
                            setStep(at, { flag: event.target.value.slice(0, 48) })
                          }
                        />
                      </label>
                      <label className="editor-field">
                        <span>Value</span>
                        <input
                          type="text"
                          value={step.flagIs}
                          placeholder="anything"
                          onChange={(event) =>
                            setStep(at, { flagIs: event.target.value.slice(0, 48) })
                          }
                        />
                      </label>
                    </>
                  ) : null}

                  {/* Everything the named triggers are not about: the hour, a
                      world flag, a status, what is standing on a tile, a
                      chance. The same builder a line of conversation and an act
                      use, because a question is a question wherever it is
                      asked. */}
                  {step.trigger === 'written' ? (
                    <>
                      <p className="hint">
                        Finished the moment all of this holds.
                        <Explain>
                          The seven above are the questions common enough to be worth a box of
                          their own. This is every other question the game can ask, written the
                          same way a line of conversation or a lever asks it.
                        </Explain>
                      </p>
                      <Conditions
                        held={step.written}
                        items={items}
                        quests={quests}
                        characters={characters}
                        objects={objects}
                        onChange={(written) => setStep(at, { written })}
                      />
                    </>
                  ) : null}

                  {step.trigger === 'deed' ? (
                    <>
                      <label className="editor-field">
                        <span>
                          Having
                          <Explain>
                            What the world writes down as it happens. Nobody has to remember to set
                            a mark, and nobody has to spell one the same way twice.
                          </Explain>
                        </span>
                        <select
                          value={step.deed}
                          onChange={(event) =>
                            setStep(at, { deed: event.target.value, deedOf: '0' })
                          }
                        >
                          {DEEDS.map((entry) => (
                            <option key={entry.deed} value={entry.deed}>
                              {entry.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="editor-field">
                        <span>Target</span>
                        {step.deed === 'firemaking' || step.deed === 'used' ? (
                          <select
                            value={step.deedOf}
                            onChange={(event) => setStep(at, { deedOf: event.target.value })}
                          >
                            <option value="0">Choose scenery</option>
                            {[...objects]
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map((one) => (
                                <option key={one.id} value={one.id}>
                                  {one.name}
                                </option>
                              ))}
                          </select>
                        ) : (
                          <ItemPicker
                            items={items}
                            value={Number(step.deedOf)}
                            onPick={(itemId) => setStep(at, { deedOf: String(itemId) })}
                          />
                        )}
                      </label>

                      <label className="editor-field">
                        <span>Count</span>
                        <input
                          type="number"
                          min={1}
                          value={step.count}
                          onChange={(event) => setStep(at, { count: event.target.value })}
                        />
                      </label>
                    </>
                  ) : null}

                  <h5>Marker</h5>
                  <label className="editor-field">
                    <span>
                      Point at
                      <Explain>
                        Left on its own it works out what the step is waiting for and points at the
                        nearest of it: the tree while logs are wanted, whoever asked for them once
                        they are carried. Name something instead and it points there whatever
                        happens.
                      </Explain>
                    </span>
                    <select
                      value={markKindOf(step)}
                      onChange={(event) => setStep(at, markCleared(event.target.value))}
                    >
                      <option value="dynamic">Automatic</option>
                      <option value="npc">Character</option>
                      <option value="object">Scenery</option>
                      <option value="item">Item Source</option>
                      <option value="tile">Tile</option>
                      <option value="place">Named Ground</option>
                    </select>
                  </label>

                  {markKindOf(step) === 'place' ? (
                    <label className="editor-field">
                      <span>
                        Place
                        <Explain>
                          The middle of the ground named, worked out as the journal is read, so
                          moving a town moves everything pointing at it.
                        </Explain>
                      </span>
                      <select
                        value={step.markPlace}
                        onChange={(event) => setStep(at, { markPlace: event.target.value })}
                      >
                        <option value="0">Choose somewhere</option>
                        {named.map((place) => (
                          <option key={place.id} value={place.id}>
                            {place.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}

                  {markKindOf(step) === 'npc' ? (
                    <label className="editor-field">
                      <span>Character</span>
                      <select
                        value={step.markNpcId}
                        onChange={(event) => setStep(at, { markNpcId: event.target.value })}
                      >
                        <option value="0">Choose somebody</option>
                        {[...characters]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((entry) => (
                            <option key={entry.id} value={entry.id}>
                              {entry.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  ) : null}

                  {markKindOf(step) === 'object' ? (
                    <label className="editor-field">
                      <span>Target</span>
                      <select
                        value={step.markObjectId}
                        onChange={(event) => setStep(at, { markObjectId: event.target.value })}
                      >
                        <option value="0">Choose one</option>
                        {[...objects]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((entry) => (
                            <option key={entry.id} value={entry.id}>
                              {entry.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  ) : null}

                  {markKindOf(step) === 'item' ? (
                    <label className="editor-field">
                      <span>
                        Which thing
                        <Explain>
                          Points at whatever yields it: the nearest tree for a log, the nearest rock
                          for an ore, or one already lying on the ground.
                        </Explain>
                      </span>
                      <ItemPicker
                        items={items}
                        value={Number(step.markItemId)}
                        onPick={(itemId) => setStep(at, { markItemId: String(itemId) })}
                      />
                    </label>
                  ) : null}

                  {markKindOf(step) === 'tile' ? (
                    <div className="editor-drop-fields">
                      <label>
                        <span>Width</span>
                        <input
                          type="number"
                          value={step.markTx}
                          onChange={(event) => setStep(at, { markTx: event.target.value })}
                        />
                      </label>
                      <label>
                        <span>Height</span>
                        <input
                          type="number"
                          value={step.markTz}
                          onChange={(event) => setStep(at, { markTz: event.target.value })}
                        />
                      </label>
                    </div>
                  ) : null}

                  <label className="editor-field">
                    <span>Note</span>
                    <input
                      type="text"
                      value={step.note}
                      placeholder="He is by the fire."
                      onChange={(event) => setStep(at, { note: event.target.value.slice(0, 120) })}
                    />
                  </label>

                  {/* Where finishing this step leads, when it does not simply lead on.
              This is what makes a quest a shape rather than a line. */}
                  <h5>
                    Where it leads
                    <Explain>
                      Nothing here means the next step, which is what most of them want. A flag and
                      a step means: if that was written down, go there instead. The first one that
                      matches decides, and nought finishes the quest.
                    </Explain>
                  </h5>

                  {step.branches.map((branch, which) => (
                    <div className="editor-brushes" key={which}>
                      <input
                        type="text"
                        value={branch.flag}
                        placeholder="If this flag"
                        onChange={(event) =>
                          setStep(at, {
                            branches: step.branches.map((one, index) =>
                              index === which
                                ? { ...one, flag: event.target.value.slice(0, 48) }
                                : one,
                            ),
                          })
                        }
                      />
                      <input
                        type="text"
                        value={branch.is}
                        placeholder="says this"
                        onChange={(event) =>
                          setStep(at, {
                            branches: step.branches.map((one, index) =>
                              index === which
                                ? { ...one, is: event.target.value.slice(0, 48) }
                                : one,
                            ),
                          })
                        }
                      />
                      <select
                        value={branch.step}
                        onChange={(event) =>
                          setStep(at, {
                            branches: step.branches.map((one, index) =>
                              index === which ? { ...one, step: event.target.value } : one,
                            ),
                          })
                        }
                      >
                        <option value="0">Finish the quest</option>
                        {draft.steps.map((other, index) => (
                          <option key={index} value={String(index + 1)}>
                            Step {index + 1}: {other.text.slice(0, 30)}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="editor-drop-remove"
                        title="Delete this one"
                        onClick={() =>
                          setStep(at, {
                            branches: step.branches.filter((_, index) => index !== which),
                          })
                        }
                      >
                        ✕
                      </button>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="wide"
                    onClick={() =>
                      setStep(at, {
                        branches: [...step.branches, { flag: '', is: '', step: String(at + 2) }],
                      })
                    }
                  >
                    Somewhere else, when something is true
                  </button>

                  {/* And what happens the moment it is finished. The same effects a
                  line of conversation carries, because a step finished by
                  chopping a tree has to be able to do something and a tree has
                  nothing to say. */}
                  <h5>
                    When it is finished
                    <Explain>
                      Run the moment the step is done, whatever finished it. Handing something over,
                      setting a flag, moving somebody: the same list a line of conversation carries.
                    </Explain>
                  </h5>

                  <Effects
                    held={step.does}
                    items={items}
                    quests={quests}
                    shops={[]}
                    characters={characters}
                    objects={objects}
                    onChange={(does: Effect[]) => setStep(at, { does })}
                  />

                  <button
                    type="button"
                    className="wide"
                    onClick={() =>
                      setStep(at, { does: [...step.does, { setFlag: '', value: 'yes' }] })
                    }
                  >
                    Do something when it is finished
                  </button>
                </>
              </div>
            );
          })()}

          {saying ? <p className="hint warning">{saying}</p> : null}
          <p className="hint">It writes itself down as you go.</p>
        </div>
      </div>
    </div>
  );
}

/** Two steps swapped, for the buttons that move one up or down. */
function swapped<T>(held: readonly T[], from: number, to: number): T[] {
  if (to < 0 || to >= held.length) return [...held];

  const next = [...held];
  const carried = next[from];
  const other = next[to];
  if (carried === undefined || other === undefined) return next;

  next[from] = other;
  next[to] = carried;
  return next;
}
